import {
	alpha,
	Box,
	Card,
	CardActionArea,
	CardContent,
	Container,
	Typography,
	useTheme
} from '@mui/material';
import { useLowResolution } from 'hooks';
import React, { FC } from 'react';
import { ITheme } from 'types/theme';

const borderRadius = 3;
const SMALL_HEIGHT = 150;
const LARGE_HEIGHT = 200;
const SMALL_WIDTH = 250;
const LARGE_WIDTH = 350;
const LOW_RES_SMALL_HEIGHT = SMALL_HEIGHT / 1.2;
const LOW_RES_LARGE_HEIGHT = LARGE_HEIGHT / 1.5;
const LOW_RES_SMALL_WIDTH = SMALL_WIDTH / 2;
const LOW_RES_LARGE_WIDTH = LARGE_WIDTH / 1.5;

const SMALL_FONT_SIZE = 17;
const LARGE_FONT_SIZE = 24;
const LOW_RES_SMALL_FONT_SIZE = 12;
const LOW_RES_LARGE_FONT_SIZE = 16;

interface IWizardCardProps {
	label: string;
	Icon: any;
	selected: boolean;
	onClick: () => void;
	size: 'small' | 'large';
	subtitle?: string;
	disabled?: boolean;
	secondarySelected?: boolean;
	cardTag?: string;
}

const WizardCard: FC<IWizardCardProps> = ({
	label,
	Icon,
	selected,
	onClick,
	size,
	subtitle,
	disabled,
	secondarySelected,
	cardTag
}) => {
	const { palette } = useTheme<ITheme>();
	const lowRes = useLowResolution();

	const backgroundStyle = { background: palette.wizard.card.main };
	const cardBorder = {
		border: `1px solid ${alpha(palette.primary.dark, 0.4)}`
	};
	let color = 'default';
	let iconColor = 'default';
	if (selected) {
		backgroundStyle.background = palette.wizard.card.light;
		cardBorder.border = `1px solid ${palette.primary.main}`;
		color = 'primary';
		iconColor = color;
	} else if (disabled) {
		backgroundStyle.background = palette.wizard.card.disabled;
		cardBorder.border = `1px solid transparent`;
		color = 'text.disabled';
		iconColor = 'disabled';
	} else if (secondarySelected) {
		cardBorder.border = `1px solid ${palette.secondary.main}`;
		backgroundStyle.background = palette.wizard.card.secondary;
		color = 'secondary';
		iconColor = color;
	}

	const hoverTransformStyle = {
		'&:hover': { transform: 'scale3d(1.05, 1.05, 1)' }
	};
	if (cardTag && (selected || secondarySelected)) {
		hoverTransformStyle['&:hover'] = { transform: '' };
	}

	return (
		<CardActionArea
			disabled={disabled}
			onClick={onClick}
			data-testid="wizard-card-action-area"
			sx={{
				height: {
					xs: size === 'small' ? LOW_RES_SMALL_HEIGHT : LOW_RES_LARGE_HEIGHT,
					sm: size === 'small' ? SMALL_HEIGHT : LARGE_HEIGHT
				},
				width: {
					xs: size === 'small' ? LOW_RES_SMALL_WIDTH : LOW_RES_LARGE_WIDTH,
					sm: size === 'small' ? SMALL_WIDTH : LARGE_WIDTH
				},
				borderRadius,
				...hoverTransformStyle
			}}
		>
			<Card
				sx={{
					width: '100%',
					height: '100%',
					borderRadius,
					...cardBorder
				}}
				elevation={disabled ? 0 : 2}
				data-testid="card"
			>
				{cardTag && (selected || secondarySelected) && (
					<Box
						position="absolute"
						sx={{
							background: palette.background.paper,
							...cardBorder,
							borderTopLeftRadius: 12,
							borderBottomRightRadius: 12,
							marginLeft: '-1px',
							marginTop: '-1px'
						}}
					>
						<Container
							maxWidth="xl"
							sx={{
								marginTop: '-3px'
							}}
						>
							<Typography variant="caption" fontWeight="bold" color={color}>
								{cardTag}
							</Typography>
						</Container>
					</Box>
				)}
				<CardContent
					data-testid="wizard-card-content"
					sx={{
						width: '100%',
						height: '100%',
						margin: 'auto',
						borderRadius,
						borderColor: palette.wizard.card.main,
						...backgroundStyle
					}}
				>
					<Box
						height="100%"
						display="flex"
						flexGrow={1}
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						textAlign="center"
					>
						{Icon && (
							<Box
								display="flex"
								flexGrow={1}
								flexDirection="column-reverse"
								alignItems="flex-start"
								justifyContent="flex-start"
								height="40%"
							>
								<Icon
									color={iconColor}
									fontSize={lowRes ? 'medium' : 'large'}
								/>
							</Box>
						)}
						<Box height="60%">
							<Typography
								variant="h6"
								fontSize={{
									xs:
										size === 'small'
											? LOW_RES_SMALL_FONT_SIZE
											: LOW_RES_LARGE_FONT_SIZE,
									sm: size === 'small' ? SMALL_FONT_SIZE : LARGE_FONT_SIZE
								}}
								fontWeight={selected || secondarySelected ? 'bold' : ''}
								color={color}
								pt={size === 'small' ? 1 : 3}
							>
								{label}
							</Typography>
							{subtitle && (
								<Typography
									variant="subtitle2"
									fontWeight={selected || secondarySelected ? 'bold' : ''}
									color={color}
									pt={1}
								>
									{subtitle}
								</Typography>
							)}
						</Box>
					</Box>
				</CardContent>
			</Card>
		</CardActionArea>
	);
};

WizardCard.defaultProps = {
	disabled: false,
	secondarySelected: false,
	cardTag: '',
	subtitle: ''
};

export default WizardCard;
